import { useCallback, useEffect, useRef, useState } from 'react';
import { getBankSearch } from '../../../utils/api_service/candi_bank_search';
import { BankSearch } from '../../../utils/types/candi_bank_types';

const POLL_INTERVAL_MS = 2000;
const SLOW_THRESHOLD_MS = 30000;
const MAX_POLL_MS = 120000;

export type PollerState = 'idle' | 'polling' | 'slow' | 'timeout' | 'completed' | 'error';

interface UseBankSearchPollerReturn {
  pollerState: PollerState;
  search: BankSearch | null;
  errorMessage: string | null;
  startPolling: (searchId: string) => void;
  stopPolling: () => void;
}

/**
 * Polls GET /candi-bank/searches/{id} every 2 s until Completed or Error.
 * Shows a "still working" state after 30 s, caps at ~2 min with timeout.
 * Cleans up on unmount. One poller per hook instance.
 */
export const useBankSearchPoller = (
  onComplete?: (search: BankSearch) => void,
  onError?: (search: BankSearch) => void,
): UseBankSearchPollerReturn => {
  const [pollerState, setPollerState] = useState<PollerState>('idle');
  const [search, setSearch] = useState<BankSearch | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);
  const searchIdRef = useRef<string | null>(null);
  const mountedRef = useRef(true);

  const cleanup = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const stopPolling = useCallback(() => {
    cleanup();
    searchIdRef.current = null;
    // Don't reset state so the last known state stays visible
  }, [cleanup]);

  const poll = useCallback(async () => {
    const id = searchIdRef.current;
    if (!id || !mountedRef.current) return;

    const elapsed = Date.now() - startTimeRef.current;

    // Cap at ~2 min
    if (elapsed > MAX_POLL_MS) {
      setPollerState('timeout');
      cleanup();
      return;
    }

    // Show "still working" after 30 s
    if (elapsed > SLOW_THRESHOLD_MS) {
      setPollerState('slow');
    }

    try {
      const response = await getBankSearch(id);
      if (!mountedRef.current) return;

      if (!response.ok) {
        // Non-2xx that isn't a data error — keep polling
        return;
      }

      const data = await response.json();
      const bankSearch: BankSearch = data.search;

      setSearch(bankSearch);

      if (bankSearch.status === 'Completed') {
        setPollerState('completed');
        setErrorMessage(null);
        cleanup();
        onComplete?.(bankSearch);
      } else if (bankSearch.status === 'Error') {
        setPollerState('error');
        setErrorMessage(bankSearch.errorMessage);
        cleanup();
        onError?.(bankSearch);
      }
      // else: In Queue or Processing — keep polling
    } catch {
      // Network error — keep polling, don't crash
    }
  }, [cleanup, onComplete, onError]);

  const startPolling = useCallback(
    (searchId: string) => {
      cleanup();
      searchIdRef.current = searchId;
      startTimeRef.current = Date.now();
      setPollerState('polling');
      setErrorMessage(null);

      // Immediate first poll
      poll();

      intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);
    },
    [cleanup, poll],
  );

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      cleanup();
    };
  }, [cleanup]);

  return {
    pollerState,
    search,
    errorMessage,
    startPolling,
    stopPolling,
  };
};
